import { useCallback, useEffect, useRef, useState } from "react";
import { PDFDocument } from "pdf-lib";
import { fileTypeFromBuffer } from "file-type";
import { useNavigate } from "react-router-dom";
import { PDFViewer } from '@embedpdf/react-pdf-viewer';

const PdfAnnotate = ({ documentURL, onSuccess, onCancel }) => {
  const [url, setUrl] = useState(documentURL);

  const hasInitializedUtilsRef = useRef(false);
  const fileInputRef = useRef(null);

  const [processedUrl, setProcessedUrl] = useState(null);
  const [imageDocUrl, setImageDocUrl] = useState(null)
  const [isMobileNative, setIsMobileNative] = useState(false)
  const [error, setError] = useState(null);
  const navigate = useNavigate()

  const viewerRef = useRef(null);
  const exportApiRef = useRef(null);

  useEffect(() => {
    window.loadPdf = (url) => {
      console.log("📥 Received URL from native:", url);
      setUrl(url);
    };
  }, []);

  const getFileType = async (url) => {
    try {
      const res = await fetch(url);
      const buffer = await res.arrayBuffer();

      const type = await fileTypeFromBuffer(buffer);

      return {
        ext: type?.ext || null,
        mime: type?.mime || null,
        buffer, // 👈 important for reuse (avoid double fetch)
      };

    } catch (e) {
      console.error("Failed to detect type", e);
      return { ext: null, mime: null, buffer: null };
    }
  };

  const convertImageToPdf = async (buffer, ext) => {
    const pdfDoc = await PDFDocument.create();

    let image;

    if (ext === "png") {
      image = await pdfDoc.embedPng(buffer);
    } else if (ext === "jpg" || ext === "jpeg") {
      image = await pdfDoc.embedJpg(buffer);
    } else {
      throw new Error(`Unsupported image format: ${ext}`);
    }

    const { width, height } = image.scale(1);

    const page = pdfDoc.addPage([width, height]);

    page.drawImage(image, {
      x: 0,
      y: 0,
      width,
      height,
    });

    const pdfBytes = await pdfDoc.save();

    return new Blob([pdfBytes], {
      type: "application/pdf",
    });
  };

  useEffect(() => {
    const prepare = async () => {
      if (!url) return;
      try {
        setError(null); // reset
        setProcessedUrl(null); // reset loading
        setImageDocUrl(null)

        const { ext, mime, buffer } = await getFileType(url);

        if (mime === "application/pdf") {
          setProcessedUrl(url);
          setImageDocUrl(null)
        }
        else if (mime?.startsWith("image/")) {
          const pdfBlob = await convertImageToPdf(buffer, ext);
          const pdfUrl = URL.createObjectURL(pdfBlob);
          setProcessedUrl(pdfUrl);
          setImageDocUrl(pdfUrl);
        }
        else {
          setProcessedUrl(null);
          setImageDocUrl(null)
          throw new Error("Unsupported type");
        }
      } catch (error) {
        console.error("Image → PDF conversion failed:", error);
        setError("Unsupported file type, only Images and PDF are supported.");
        setProcessedUrl(null);
        setImageDocUrl(null)
      }
    };
    prepare();
  }, [url]);

  useEffect(() => {
    //Hiding chatwoot from both desktop and mobile
    document.body.classList.add('hide-chatwoot-mobile')
    if (window.chatwootSDK && window.chatwootSDK.on) {
      window.chatwootSDK.on('ready', () => {
        console.log('Chatwoot is ready (UI hidden via CSS).')
      })
    }

    return () => {
      document.body.classList.remove('hide-chatwoot-mobile')
    }
  }, [])

  useEffect(() => {
    hasInitializedUtilsRef.current = false;
  }, [url]);

  useEffect(() => {
    window.loadPdf = (url) => {
      console.log("📥 Received URL from native:", url);
      setUrl(url);
    };
  }, []);

  useEffect(() => {
    const isAndroid = !!window.Android;
    const isIOS = !!window.webkit?.messageHandlers;

    const isNative = isAndroid || isIOS;

    if (!isNative && !documentURL) {
      navigate('/');
    }
    setIsMobileNative(isNative)
  }, [documentURL])

  const handleDismissOnNative = async () => {
    // ANDROID
    if (window.Android) {
      window.Android.onDismiss();
      return
    }

    // iOS (WKWebView)
    if (window.webkit?.messageHandlers?.onDismiss) {
      window.webkit.messageHandlers.onDismiss.postMessage("dismiss");
      return
    }
  }

  // const handleExportPdf = async () => {
  //   try {
  //     completeDrawingIfActive()
  //     await new Promise((resolve) => setTimeout(resolve, 0));
  //     const pdfBytes = await exportPdf(imageDocUrl ?? processedUrl, highlights);

  //     const base64 = btoa(
  //       new Uint8Array(pdfBytes).reduce(
  //         (data, byte) => data + String.fromCharCode(byte),
  //         ""
  //       )
  //     );

  //     // ANDROID
  //     if (window.Android) {
  //       window.Android.onPdfExport(base64);
  //       return
  //     }

  //     // iOS (WKWebView)
  //     if (window.webkit?.messageHandlers?.pdfExport) {
  //       window.webkit.messageHandlers.pdfExport.postMessage(base64);
  //       return
  //     }

  //     // web
  //     const blob = new Blob([pdfBytes], { type: "application/pdf" });
  //     if (!onSuccess) {
  //       const downloadUrl = URL.createObjectURL(blob);

  //       const a = document.createElement("a");
  //       a.href = downloadUrl;
  //       a.download = "annotated-document.pdf";
  //       a.click();

  //       URL.revokeObjectURL(downloadUrl);
  //     } else {
  //       const file = new File([blob], `annotated-${Date.now()}.pdf`, {
  //         type: "application/pdf",
  //       });

  //       onSuccess(file);
  //     }
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  function isGroupItem(item) {
    return item.type === 'group'
  }

  const handleExportPdf = async () => {
    try {
      if (!exportApiRef.current) {
        console.error("Export plugin is not ready.");
        return;
      }

      // Returns ArrayBuffer of the updated PDF
      const arrayBuffer = await exportApiRef.current
        .saveAsCopy()
        .toPromise();

      // ---------- Android / iOS ----------
      const bytes = new Uint8Array(arrayBuffer);

      const base64 = btoa(
        bytes.reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );

      if (window.Android) {
        window.Android.onPdfExport(base64);
        return;
      }

      if (window.webkit?.messageHandlers?.pdfExport) {
        window.webkit.messageHandlers.pdfExport.postMessage(base64);
        return;
      }

      // ---------- Web ----------
      const blob = new Blob([arrayBuffer], {
        type: "application/pdf",
      });

      if (!onSuccess) {
        const downloadUrl = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = "annotated-document.pdf";
        a.click();

        URL.revokeObjectURL(downloadUrl);
      } else {
        const file = new File(
          [blob],
          `annotated-${Date.now()}.pdf`,
          {
            type: "application/pdf",
          }
        );

        onSuccess(file);
      }
    } catch (e) {
      console.error(e);
    }
  };


  const handleReady = useCallback(async () => {

    const container = viewerRef.current?.container
    if (!container) return

    const registry = await container.registry

    const commands = registry.getPlugin('commands')?.provides()
    const ui = registry.getPlugin('ui')?.provides()

    const exportPlugin = registry.getPlugin("export")?.provides();

    exportApiRef.current = exportPlugin;

    if (!commands || !ui) return


    // ─────────────────────────────────────────────────────────
    // 1. Register custom icons (stroke-based Tabler icons)
    // ─────────────────────────────────────────────────────────
    container.registerIcons({
      customSave: {
        viewBox: "0 0 24 24",
        paths: [
          {
            d: "M5 12h11",
            stroke: "#1976d2",
            strokeWidth: 2,
            strokeLinecap: "round",
            fill: "none",
          },
          {
            d: "M12 5l7 7-7 7",
            stroke: "#1976d2",
            strokeWidth: 2,
            strokeLinecap: "round",
            strokeLinejoin: "round",
            fill: "none",
          },
        ],
      },
      customCancel: {
        viewBox: "0 0 24 24",
        paths: [
          {
            d: "M18 6L6 18",
            stroke: "#d32f2f",
            strokeWidth: 2.5,
            strokeLinecap: "round",
            fill: "none",
          },
          {
            d: "M6 6L18 18",
            stroke: "#d32f2f",
            strokeWidth: 2.5,
            strokeLinecap: "round",
            fill: "none",
          },
        ],
      },
    })

    // ─────────────────────────────────────────────────────────
    // 2. Register custom commands
    // ─────────────────────────────────────────────────────────
    commands.registerCommand({
      id: 'custom.save',
      label: 'Save',
      icon: 'customSave',
      action: async () => {
        await handleExportPdf();
      },
    })

    commands.registerCommand({
      id: 'custom.cancel',
      label: 'Cancel & Close',
      icon: 'customCancel',
      action: async () => {
        await handleDismissOnNative()
        onCancel()
      },
    })

    // ─────────────────────────────────────────────────────────
    // 3. Modify the UI: Replace comment button with smiley
    // ─────────────────────────────────────────────────────────
    const currentSchema = ui.getSchema()
    const mainToolbar = currentSchema.toolbars['main-toolbar']

    if (mainToolbar) {
      // Clone items with proper typing using structuredClone
      const items = structuredClone(mainToolbar.items);

      const leftGroup = items.find(
        (item) =>
          isGroupItem(item) && item.id === "left-group"
      );

      if (leftGroup) {
        const documentIndex = leftGroup.items.findIndex(
          (item) => item.id === "document-menu-button"
        );

        if (documentIndex !== -1) {
          leftGroup.items[documentIndex] = {
            type: "command-button",
            id: "cancel-button",
            commandId: "custom.cancel",
            variant: "icon",
          };
        }
      }


      // Find the right-group using type guard
      const rightGroup = items.find(
        (item) =>
          isGroupItem(item) && item.id === 'right-group',
      )

      if (rightGroup) {
        // Find and replace the comment button with our smiley button
        const commentIndex = rightGroup.items.findIndex(
          (item) => item.id === 'comment-button',
        )

        if (commentIndex !== -1) {
          rightGroup.items[commentIndex] = {
            type: "command-button",
            id: "save-button",
            commandId: "custom.save",
            variant: "icon",
          }
        }
      }

      ui.mergeSchema({
        toolbars: {
          'main-toolbar': {
            ...mainToolbar,
            items,
          },
        },
      })
    }

  }, [])

  return (
    <div style={{
      height: isMobileNative ? "100vh" : "90vh",
      paddingTop: isMobileNative ? (!!window.webkit?.messageHandlers ? "20px" : "50px") : 0,
      paddingBottom: isMobileNative ? "env(safe-area-inset-bottom)" : 0,
      boxSizing: "border-box",
    }}>
      {error && (
        <div className="edvi-pdf-annotate-chrome">
          <p className="edvi-pdf-annotate-chrome__msg">{error}</p>
          <div>
            <button
              type="button"
              onClick={isMobileNative ? handleDismissOnNative : onCancel}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                background: "#1976d2",
                color: "white",
                cursor: "pointer",
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* ⏳ LOADING STATE */}
      {!error && !processedUrl && (
        <div className="edvi-pdf-annotate-chrome">
          <p className="edvi-pdf-annotate-chrome__msg">Loading document...</p>
        </div>
      )}

      {processedUrl && <PDFViewer
        ref={viewerRef}
        style={{
          flex: 1,
          width: "100%",
          height: "100%",
        }}
        config={{
          src: processedUrl,
          theme: { preference: 'light' },
          disabledCategories: [
            "document-open",
            "document-close",
            "document-print",
            "document-capture",
            "document-export",
            "document-fullscreen",
            "document-protect",
            "panel-search",
            "page", "spread", "rotate", "navigation", "zoom",
            //"panel-sidebar",
          ],
        }}
        onReady={handleReady}
      />}
    </div>
  );
};

export default PdfAnnotate;